 
 
 
 
 
 
import { BUILDINGS_CONSTANTS, getMonthName } from '@/config/gameConstants';
import type { 
  BuildingType, 
  OwnedBuilding, 
  BuildingCapacity, 
  BuildingCosts 
} from '@/types/buildings';
import type { Employee, HiringProcess } from '@/types/tycoon';

export class BuildingService {
  private static instance: BuildingService;
  
  private constructor() {}
  
  static getInstance(): BuildingService {
    if (!BuildingService.instance) {
      BuildingService.instance = new BuildingService();
    }
    return BuildingService.instance;
  }

  // Get all building types
  getAllBuildingTypes(): BuildingType[] {
    return Object.values(BUILDINGS_CONSTANTS.types);
  }

  // Check if an unlock date has been reached
  isUnlockDateReached(
    unlockDate: { year: number; month: number },
    currentDate: { year: number; month: number }
  ): boolean {
    // Validate month is in valid range (1-12)
    if (unlockDate.month < 1 || unlockDate.month > 12 || currentDate.month < 1 || currentDate.month > 12) {
      console.error('Invalid month in date comparison', { unlockDate, currentDate });
      return false;
    }
    if (currentDate.year > unlockDate.year) return true;
    if (currentDate.year < unlockDate.year) return false;
    return currentDate.month >= unlockDate.month;
  }

  // Get building types available based on current date (year and month)
  getAvailableBuildingTypes(currentDate: { year: number; month: number }): BuildingType[] {
    return this.getAllBuildingTypes().filter(
      building => this.isUnlockDateReached(building.unlockDate, currentDate)
    );
  }

  // Get a specific building type
  getBuildingType(buildingTypeId: string): BuildingType | undefined {
    return BUILDINGS_CONSTANTS.types[buildingTypeId as keyof typeof BUILDINGS_CONSTANTS.types];
  }

  // Check if a building can be purchased
  canPurchaseBuilding(
    buildingTypeId: string,
    cash: number,
    currentDate: { year: number; month: number },
    ownedBuildings: OwnedBuilding[]
    // reputation parameter removed - may be added back later
  ): {
    canPurchase: boolean;
    reason?: string;
  } {
    const buildingType = this.getBuildingType(buildingTypeId);

    if (!buildingType) {
      return { canPurchase: false, reason: 'Invalid building type' };
    }

    // Check if unlock date has been reached
    if (!this.isUnlockDateReached(buildingType.unlockDate, currentDate)) {
      return {
        canPurchase: false,
        reason: `Unlocks in ${getMonthName(buildingType.unlockDate.month)} ${buildingType.unlockDate.year}`
      };
    }

    // Reputation requirement check - COMMENTED OUT, may be added back later
    // if (buildingType.reputationRequirement && reputation < buildingType.reputationRequirement) {
    //   return { canPurchase: false, reason: `Requires ${buildingType.reputationRequirement} reputation (current: ${Math.floor(reputation)})` };
    // }

    if (cash < buildingType.cost) {
      return { canPurchase: false, reason: 'Insufficient funds' };
    }

    // Check HQ limit (max 1 of each headquarters type)
    if (buildingType.category === 'headquarters') {
      const hasThisHQ = ownedBuildings.some(b => b.buildingTypeId === buildingTypeId);
      if (hasThisHQ) {
        return { canPurchase: false, reason: `You can only own one ${buildingType.name}` };
      }
    }

    return { canPurchase: true };
  }

  // Calculate total capacity from owned buildings
  calculateCapacity(ownedBuildings: OwnedBuilding[], employees: Employee[]): BuildingCapacity {
    const capacity: BuildingCapacity = {
      total: 0,
      used: 0,
      available: 0,
      byCategory: {
        office: { total: 0, used: 0 },
        factory: { total: 0, used: 0 },
        lab: { total: 0, used: 0 }
      }
    };

    // Calculate total capacity by category (only count completed buildings)
    ownedBuildings.forEach(building => {
      if (!building.isUnderConstruction) {
        const buildingType = this.getBuildingType(building.buildingTypeId);
        if (buildingType) {
          // Add office capacity
          if (buildingType.capacity.officeWorkers > 0) {
            capacity.byCategory.office.total += buildingType.capacity.officeWorkers;
            capacity.total += buildingType.capacity.officeWorkers;
          }
          
          // Add factory capacity
          if (buildingType.capacity.factoryWorkers > 0) {
            capacity.byCategory.factory.total += buildingType.capacity.factoryWorkers;
            capacity.total += buildingType.capacity.factoryWorkers;
          }
          
          // Add lab capacity
          if (buildingType.capacity.technicalWorkers > 0) {
            capacity.byCategory.lab.total += buildingType.capacity.technicalWorkers;
            capacity.total += buildingType.capacity.technicalWorkers;
          }
        }
      }
    });

    // Calculate used capacity by employee role
    employees?.forEach(employee => {
      const category = this.getRoleBuildingCategory(employee.role);
      
      if (category) {
        capacity.used++;
        capacity.byCategory[category].used++;
      }
    });

    // Update available capacity
    capacity.available = capacity.total - capacity.used;
    
    // Update available capacity for each category
    for (const category in capacity.byCategory) {
      const cat = capacity.byCategory[category as keyof typeof capacity.byCategory];
      cat.available = cat.total - cat.used;
    }
    
    return capacity;
  }

  // Get all roles allowed in a building category
  getAllowedRolesForCategory(category: 'headquarters' | 'office' | 'factory' | 'lab'): string[] {
    const allowedRoles: string[] = [];
    const categoryInfo = BUILDINGS_CONSTANTS.categories[category];
    
    if (categoryInfo) {
      // Get all roles from the allowed groups
      categoryInfo.allowedGroups.forEach(groupKey => {
        const group = BUILDINGS_CONSTANTS.employeeGroups[groupKey as keyof typeof BUILDINGS_CONSTANTS.employeeGroups];
        if (group) {
          allowedRoles.push(...group.roles);
        }
      });
    }
    
    return allowedRoles;
  }

  // Get which building category a role belongs to
  private getRoleBuildingCategory(role: string): 'headquarters' | 'office' | 'factory' | 'lab' | null {
    // Find which employee group this role belongs to
    for (const [groupKey, group] of Object.entries(BUILDINGS_CONSTANTS.employeeGroups)) {
      if (group && Array.isArray(group.roles) && group.roles.includes(role)) {
        // Found the role, now find which building categories allow this group
        // Skip headquarters for employee categorization (headquarters is mixed-use)
        for (const [categoryKey, category] of Object.entries(BUILDINGS_CONSTANTS.categories)) {
          if (categoryKey !== 'headquarters' && category && Array.isArray(category.allowedGroups) && category.allowedGroups.includes(groupKey)) {
            return categoryKey as 'headquarters' | 'office' | 'factory' | 'lab';
          }
        }
      }
    }
    return null;
  }

  // Check if there's capacity for hiring more employees (alias for backwards compatibility)
  checkCapacityForHiring(
    ownedBuildings: OwnedBuilding[],
    employees: Employee[],
    role: string,
    count: number
  ): {
    hasCapacity: boolean;
    availableCapacity: number;
    reason?: string;
  } {
    return this.hasCapacityForRole(role, count, ownedBuildings, employees);
  }

  // Check if there's capacity for hiring more employees
  hasCapacityForRole(
    role: string, 
    count: number, 
    ownedBuildings: OwnedBuilding[], 
    employees: Employee[],
    hiringProcesses?: HiringProcess[]
  ): {
    hasCapacity: boolean;
    availableCapacity: number;
    reason?: string;
  } {
    const capacity = this.calculateCapacity(ownedBuildings, employees);
    const category = this.getRoleBuildingCategory(role);

    if (!category) {
      return { 
        hasCapacity: false, 
        availableCapacity: 0,
        reason: 'Invalid employee role' 
      };
    }

    // Calculate pending hires for this category
    let pendingHiresInCategory = 0;
    if (hiringProcesses && hiringProcesses.length > 0) {
      hiringProcesses.forEach(process => {
        const processCategory = this.getRoleBuildingCategory(process.role);
        if (processCategory === category) {
          pendingHiresInCategory += process.count;
        }
      });
    }

    const categoryCapacity = capacity.byCategory[category];
    const currentlyUsed = categoryCapacity.used;
    const totalCommitted = currentlyUsed + pendingHiresInCategory;
    const available = categoryCapacity.total - totalCommitted;

    if (available < count) {
      return {
        hasCapacity: false,
        availableCapacity: Math.max(0, available),
        reason: `Not enough ${BUILDINGS_CONSTANTS.categories[category].name} capacity. Need ${count} but only ${available} available (${currentlyUsed} current employees + ${pendingHiresInCategory} pending hires = ${totalCommitted} total committed).`
      };
    }

    return { hasCapacity: true, availableCapacity: available };
  }

  // Calculate monthly maintenance costs
  calculateMaintenanceCosts(ownedBuildings: OwnedBuilding[]): BuildingCosts {
    const costs: BuildingCosts = {
      totalMaintenance: 0,
      byBuilding: []
    };

    ownedBuildings.forEach(building => {
      const buildingType = this.getBuildingType(building.buildingTypeId);
      if (buildingType) {
        costs.totalMaintenance += buildingType.maintenanceCost;
        costs.byBuilding.push({
          buildingId: building.id,
          buildingName: buildingType.name,
          maintenanceCost: buildingType.maintenanceCost
        });
      }
    });

    return costs;
  }

  // Calculate bonuses from buildings (placeholder for future implementation)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  calculateBonuses(_ownedBuildings: OwnedBuilding[]): {
    reputation: number;
    productivity: number;
    productionCost: number;
    productionSpeed: number;
    quality: number;
    researchSpeed: number;
    researchPoints: number;
  } {
    const bonuses = {
      reputation: 0,
      productivity: 0,
      productionCost: 0,
      productionSpeed: 0,
      quality: 0,
      researchSpeed: 0,
      researchPoints: 0
    };

    return bonuses;
  }

  // Create a new owned building
  purchaseBuilding(
    buildingTypeId: string, 
    gameDate: { day: number; month: number; year: number }
  ): OwnedBuilding {
    const buildingType = this.getBuildingType(buildingTypeId);
    const constructionTime = buildingType?.constructionTime || 0;
    
    const constructionCompleteDate = constructionTime > 0 
      ? this.addDaysToDate(gameDate, constructionTime)
      : gameDate;

    return {
      id: `building_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      buildingTypeId,
      purchaseDate: { ...gameDate },
      constructionCompleteDate: constructionTime > 0 ? constructionCompleteDate : undefined,
      isUnderConstruction: constructionTime > 0
    };
  }

  // Helper to add days to a date
  private addDaysToDate(
    date: { day: number; month: number; year: number }, 
    daysToAdd: number
  ): { day: number; month: number; year: number } {
    let { day, month, year } = date;
    day += daysToAdd;

    // Simple date calculation (assumes 30 days per month for game purposes)
    while (day > 30) {
      day -= 30;
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }

    return { day, month, year };
  }

  // Get capacity warning level
  getCapacityWarningLevel(capacity: BuildingCapacity): 'none' | 'low' | 'critical' {
    if (capacity.total === 0) return 'critical';
    
    const usageRatio = capacity.used / capacity.total;
    
    if (usageRatio >= BUILDINGS_CONSTANTS.capacityWarnings.critical) {
      return 'critical';
    } else if (usageRatio >= BUILDINGS_CONSTANTS.capacityWarnings.low) {
      return 'low';
    }
    
    return 'none';
  }

  // Check if a building construction is complete
  isConstructionComplete(
    building: OwnedBuilding, 
    currentDate: { day: number; month: number; year: number }
  ): boolean {
    if (!building.isUnderConstruction || !building.constructionCompleteDate) {
      return true;
    }

    return this.isDateAfterOrEqual(currentDate, building.constructionCompleteDate);
  }

  // Helper to compare dates
  private isDateAfterOrEqual(
    date1: { day: number; month: number; year: number },
    date2: { day: number; month: number; year: number }
  ): boolean {
    if (date1.year > date2.year) return true;
    if (date1.year < date2.year) return false;
    if (date1.month > date2.month) return true;
    if (date1.month < date2.month) return false;
    return date1.day >= date2.day;
  }

  // Update construction status for buildings
  updateConstructionStatus(
    buildings: OwnedBuilding[],
    currentDate: { day: number; month: number; year: number }
  ): OwnedBuilding[] {
    return buildings.map(building => {
      if (building.isUnderConstruction && this.isConstructionComplete(building, currentDate)) {
        return {
          ...building,
          isUnderConstruction: false
        };
      }
      return building;
    });
  }

  // Get construction progress
  getConstructionProgress(
    building: OwnedBuilding,
    currentDate: { day: number; month: number; year: number }
  ): { progress: number; daysRemaining: number } {
    if (!building.isUnderConstruction || !building.constructionCompleteDate) {
      return { progress: 100, daysRemaining: 0 };
    }

    const buildingType = this.getBuildingType(building.buildingTypeId);
    if (!buildingType) {
      return { progress: 0, daysRemaining: 0 };
    }

    const totalDays = buildingType.constructionTime;
    const daysElapsed = this.getDaysBetween(building.purchaseDate, currentDate);
    const daysRemaining = Math.max(0, totalDays - daysElapsed);
    const progress = Math.min(100, (daysElapsed / totalDays) * 100);

    return { progress, daysRemaining };
  }

  // Check if a building can be upgraded
  canUpgradeBuilding(
    building: OwnedBuilding,
    cash: number,
    currentDate: { year: number; month: number }
  ): {
    canUpgrade: boolean;
    upgradeToId?: string;
    cost?: number;
    reason?: string;
  } {
    const buildingType = this.getBuildingType(building.buildingTypeId);

    if (!buildingType) {
      return { canUpgrade: false, reason: 'Invalid building type' };
    }

    // Building must be completed before upgrading
    if (building.isUnderConstruction) {
      return { canUpgrade: false, reason: 'Building is still under construction' };
    }

    // Check if building is already being upgraded
    if (building.isUpgrading) {
      return { canUpgrade: false, reason: 'Building is already being upgraded' };
    }

    // Check if building has an upgrade path
    if (!buildingType.upgradesTo) {
      return { canUpgrade: false, reason: 'This building cannot be upgraded further' };
    }

    const upgradeToBuildingType = this.getBuildingType(buildingType.upgradesTo);
    if (!upgradeToBuildingType) {
      return { canUpgrade: false, reason: 'Invalid upgrade target' };
    }

    // Check if upgrade is unlocked by date
    if (!this.isUnlockDateReached(upgradeToBuildingType.unlockDate, currentDate)) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return {
        canUpgrade: false,
        reason: `Upgrade unlocks in ${monthNames[upgradeToBuildingType.unlockDate.month - 1]} ${upgradeToBuildingType.unlockDate.year}`
      };
    }

    // Check if player has enough funds
    const upgradeCost = buildingType.upgradeCost || 0;
    if (cash < upgradeCost) {
      return { canUpgrade: false, reason: 'Insufficient funds for upgrade' };
    }

    return {
      canUpgrade: true,
      upgradeToId: buildingType.upgradesTo,
      cost: upgradeCost
    };
  }

  // Start building upgrade
  startUpgrade(
    building: OwnedBuilding,
    gameDate: { day: number; month: number; year: number }
  ): OwnedBuilding {
    const buildingType = this.getBuildingType(building.buildingTypeId);
    if (!buildingType || !buildingType.upgradesTo) {
      return building;
    }

    const upgradeTime = buildingType.upgradeTime || 0;
    const upgradeCompleteDate = upgradeTime > 0 
      ? this.addDaysToDate(gameDate, upgradeTime)
      : gameDate;

    return {
      ...building,
      isUpgrading: true,
      upgradeToId: buildingType.upgradesTo,
      upgradeStartDate: { ...gameDate },
      upgradeCompleteDate: upgradeTime > 0 ? upgradeCompleteDate : undefined
    };
  }

  // Complete building upgrade
  completeUpgrade(building: OwnedBuilding): OwnedBuilding {
    if (!building.isUpgrading || !building.upgradeToId) {
      return building;
    }

    return {
      ...building,
      buildingTypeId: building.upgradeToId,
      isUpgrading: false,
      upgradeToId: undefined,
      upgradeStartDate: undefined,
      upgradeCompleteDate: undefined
    };
  }

  // Check if upgrade is complete
  isUpgradeComplete(
    building: OwnedBuilding,
    currentDate: { day: number; month: number; year: number }
  ): boolean {
    if (!building.isUpgrading || !building.upgradeCompleteDate) {
      return false;
    }

    return this.isDateAfterOrEqual(currentDate, building.upgradeCompleteDate);
  }

  // Get upgrade progress
  getUpgradeProgress(
    building: OwnedBuilding,
    currentDate: { day: number; month: number; year: number }
  ): { progress: number; daysRemaining: number } {
    if (!building.isUpgrading || !building.upgradeStartDate || !building.upgradeCompleteDate) {
      return { progress: 0, daysRemaining: 0 };
    }

    const buildingType = this.getBuildingType(building.buildingTypeId);
    if (!buildingType || !buildingType.upgradeTime) {
      return { progress: 0, daysRemaining: 0 };
    }

    const totalDays = buildingType.upgradeTime;
    const daysElapsed = this.getDaysBetween(building.upgradeStartDate, currentDate);
    const daysRemaining = Math.max(0, totalDays - daysElapsed);
    const progress = Math.min(100, (daysElapsed / totalDays) * 100);

    return { progress, daysRemaining };
  }

  // Update upgrade status for buildings
  updateUpgradeStatus(
    buildings: OwnedBuilding[],
    currentDate: { day: number; month: number; year: number }
  ): OwnedBuilding[] {
    return buildings.map(building => {
      if (building.isUpgrading && this.isUpgradeComplete(building, currentDate)) {
        return this.completeUpgrade(building);
      }
      return building;
    });
  }

  // Helper to get days between two dates
  private getDaysBetween(
    date1: { day: number; month: number; year: number },
    date2: { day: number; month: number; year: number }
  ): number {
    // Simplified calculation assuming 30 days per month
    const days1 = date1.year * 360 + (date1.month - 1) * 30 + date1.day;
    const days2 = date2.year * 360 + (date2.month - 1) * 30 + date2.day;
    return Math.abs(days2 - days1);
  }
}

export const buildingService = BuildingService.getInstance();